import { useAuthStore } from "@/features/auth/store";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

// IR-1042 (reopened 2026-05-25): when many widgets poll concurrently and the
// access_token expires, every 401 used to spawn its own POST /auth/refresh/.
// N parallel callers = N concurrent BE rotations of the same refresh_token,
// racing the IR-646 replay detector and killing the session chain
// (REFRESH_TOKEN_REPLAY_DETECTED on the backend). A single module-scope
// in-flight promise lets concurrent callers share one upstream refresh; the
// trailing 2 s window catches late stragglers from the same 401-wave.
let refreshInFlight: Promise<boolean> | null = null;

// Test-only — drop the in-flight promise so a subsequent test starts clean.
// The 2 s clear-timer in production code can't run between vitest cases.
export function __resetRefreshInFlightForTests(): void {
  refreshInFlight = null;
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const resp = await fetch(`${BASE}/auth/refresh/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      return resp.ok;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 2000);
    }
  })();
  return refreshInFlight;
}

// IR-1241: public wrapper so the idle-warning "Stay signed in" button can
// proactively extend the session. Reuses refreshAccessToken() so it shares the
// same IR-1042 single-flight promise as the 401-triggered refreshes.
export function refreshSession(): Promise<boolean> {
  return refreshAccessToken();
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { skipRefresh?: boolean } = {},
): Promise<T> {
  const { skipRefresh, ...rest } = init;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(rest.headers as Record<string, string> | undefined),
  };

  const resp = await fetch(`${BASE}${path}`, { ...rest, headers, credentials: "include" });

  if (resp.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      useAuthStore.getState().clear();
      const body = await safeJson(resp);
      throw new ApiError(401, body, "refresh-failed");
    }
    return api<T>(path, { ...init, skipRefresh: true });
  }

  if (!resp.ok) {
    const body = await safeJson(resp);
    throw new ApiError(resp.status, body, `HTTP ${resp.status}`);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

async function safeJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

/**
 * Like `api`, but returns the raw response body as a Blob — for file downloads
 * (the Join Links CSV export, IR-1047). Shares the same cookie-auth +
 * single-flight 401→refresh→retry behaviour as `api`, so an access token that
 * expired mid-session still yields a clean download instead of a failed click.
 * No JSON Content-Type header is sent (this is a GET download).
 */
export async function apiBlob(
  path: string,
  init: RequestInit & { skipRefresh?: boolean } = {},
): Promise<Blob> {
  const { skipRefresh, ...rest } = init;
  const resp = await fetch(`${BASE}${path}`, { ...rest, credentials: "include" });

  if (resp.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      useAuthStore.getState().clear();
      throw new ApiError(401, null, "refresh-failed");
    }
    return apiBlob(path, { ...init, skipRefresh: true });
  }

  if (!resp.ok) {
    const body = await safeJson(resp);
    throw new ApiError(resp.status, body, `HTTP ${resp.status}`);
  }

  return await resp.blob();
}
